/**
 * Level 3, Lesson 1: The Math Map, Number Bonds & The Nines — from lesson1_nines.tex.
 * Every question is the book's question, asked one at a time; the book's sentence is the reveal after the student
 * answers it. The nines themselves run in the Times Tables unit (t9), with the 11th and 12th facts (t1112).
 */
import type { Item, Lesson } from "../types";
import { fingers, mathMap, roundingLine } from "../visuals";
import { add9Item, roundItem } from "../skills/level3-l1";
import { seriesSub9, subMapItem } from "../skills/addsub987";
import { mapPuzzleUp } from "../visuals";

const q = (o: Omit<Item, "skill"> & { skill?: string }): Item => ({ skill: "map-discovery-q", ...o });
const is = (raw: string, n: number) => raw.trim() === String(n);

const mapItems: Item[] = [
  q({
    id: "l3.1:map-top-row",
    prompt: "This is the Math Map. It looks like a hundreds board, but it starts with <b>zero</b>. How many numbers are on the top row?",
    visual: mathMap({ row: 0 }),
    input: { kind: "number" },
    answer: "10",
    diagnose: (r) => (is(r, 9) ? { code: "map-forgot-zero", hint: "Count again — zero is a number. It counts." } : null),
    hints: ["Point to each number on the top row and count them. Start with the 0.", "0, 1, 2, … 9 — count every one, zero too.", "0, 1, 2, 3, 4, 5, 6, 7, 8, 9: that is ten numbers, because zero counts."],
    reveal: "Ten. Zero is a number, and it counts.",
  }),
  q({
    id: "l3.1:map-column-8",
    prompt: "Look at the column with 7 at the top. What does every number in that column end with?",
    visual: mathMap({ col: 7 }),
    input: { kind: "number" },
    answer: "7",
    hints: ["Read the column down: 7, 17, 27, 37…", "Look only at the last digit of each one.", "7, 17, 27, 37 — every one ends with 7."],
    reveal: "Every one ends with 7. <b>The column tells you the ones digit.</b>",
  }),
  q({
    id: "l3.1:map-row-40s",
    prompt: "Look at the 60s row. What do all its numbers begin with?",
    visual: mathMap({ row: 6 }),
    input: { kind: "number" },
    answer: "6",
    hints: ["Read the row across: 60, 61, 62…", "Look only at the first digit of each one.", "60, 61, 62, 63 — every one begins with 6."],
    reveal: "They all begin with 6. <b>The row tells you the tens digit.</b>",
  }),
  q({
    id: "l3.1:map-even-columns",
    prompt: "Look at the blue (shaded) columns: 0, 2, 4, 6, 8. Are those numbers even or odd?",
    visual: mathMap({}),
    input: { kind: "choice", options: ["even", "odd"] },
    answer: "even",
    hints: ["Can 2, 4, 6 and 8 be split into two equal groups?", "The shaded columns are the ones you get counting by twos from zero.", "0, 2, 4, 6, 8 — counting by twos. They are even."],
    reveal: "Even. When you learn an even times table, you will use only the blue columns.",
  }),
];

const bondItems: Item[] = [9, 8, 7, 6, 5].map((up) =>
  q({
    id: `l3.1:fingers-${up}`,
    prompt: `Hold up ${up} fingers. How many are down?`,
    visual: fingers(up),
    input: { kind: "number" },
    answer: String(10 - up),
    diagnose: (r) => (up !== 5 && is(r, up) ? { code: "bond-said-up", hint: `${up} are up. How many are DOWN?` } : null),
    hints: ["Look at the fingers that are folded down.", "Count only the folded-down fingers.", `${up} up and ${10 - up} down.`],
    reveal: up === 5 ? "Five up, five down. <b>5's bond is 5</b> — it is its own bond." : `<b>${up}'s bond is ${10 - up}.</b> Together they make ten.`,
  }),
);

const ninesItems: Item[] = [
  q({
    id: "l3.1:nines-first",
    prompt: "<em>Times</em> means how many times you counted. Start at zero on the Map and count <b>9 steps</b>. Where did you land?",
    visual: mathMap({ mark: [0] }),
    input: { kind: "number" },
    answer: "9",
    diagnose: (r) => (is(r, 8) ? { code: "count-step-on", hint: "You never count the square you start on. Start at 0, then step: 1, 2, 3…" } : is(r, 10) ? { code: "count-step-extra", hint: "Count the steps again: the first step lands on 1." } : null),
    hints: ["The first step lands on 1.", "Step 1, 2, 3, 4, 5, 6, 7, 8, 9.", "Nine steps from zero lands on 9."],
    reveal: "9. You just proved that 9 × 1 = 9.",
  }),
  q({
    id: "l3.1:nines-second",
    prompt: "Now count forward <b>nine more steps</b> from 9. Where do you land?",
    visual: mathMap({ ring: [9] }),
    input: { kind: "number" },
    answer: "18",
    hints: ["Start on 9 and step: 10, 11, 12…", "Nine steps: 10, 11, 12, 13, 14, 15, 16, 17, 18.", "From 9, nine more steps lands on 18."],
    reveal: "18. That's 9 × 2.",
  }),
  q({
    id: "l3.1:nines-shortcut",
    prompt: "Look at your two circles, 9 and 18. From the first to the second, how many rows did you move down?",
    visual: mathMap({ ring: [9, 18] }),
    input: { kind: "number" },
    answer: "1",
    hints: ["9 is on the top row. Where is 18?", "18 is on the teens row, right under the top row.", "From the top row to the teens row is one row down."],
    reveal: "One row down.",
  }),
  q({
    id: "l3.1:nines-shortcut-2",
    prompt: "And which way did you move in the row — back (left) or forward (right)?",
    visual: mathMap({ ring: [9, 18] }),
    input: { kind: "choice", options: ["back 1", "forward 1"] },
    answer: "back 1",
    hints: ["9 is in the 9 column. Which column is 18 in?", "The 8 column is one to the left of the 9 column.", "From the 9 column to the 8 column is back 1."],
    reveal: "The shortcut is nine's number bond: <b>back 1, then down a row.</b>",
  }),
  q({
    id: "l3.1:nines-circle",
    prompt: "The moment you see the shortcut, you may use it. <b>Circle every nine</b>, all the way to 90: tap each one on the Map.",
    visual: "",
    input: {
      kind: "tap",
      rows: 10,
      targets: [9, 18, 27, 36, 45, 54, 63, 72, 81, 90],
      start: [9, 18],
      wrongTap: (n, found) => {
        const last = Math.max(...found);
        return `${n} isn't a nine. From ${last}: back 1, then down a row.`;
      },
    },
    answer: "9,18,27,36,45,54,63,72,81,90",
    hints: ["From 18: back 1, then down a row.", "Each nine is one column left and one row down from the last.", "9, 18, 27, 36, 45, 54, 63, 72, 81, 90."],
    reveal: "Ten circles, a diagonal down the Map. Count them out loud — forward <em>and</em> backward — three times. Counting backward matters just as much as counting forward.",
  }),
  q({
    id: "l3.1:nines-code",
    prompt: "Use the shortcut to circle every nine: 9, 18, 27… Read just the last digit of each one, from the top. What do the digits do?",
    visual: mathMap({ ring: [9, 18, 27, 36, 45, 54, 63, 72, 81, 90] }),
    input: { kind: "choice", options: ["count up", "count down", "stay the same"] },
    answer: "count down",
    hints: ["Read the ones digits: 9, 8, 7…", "9, 8, 7, 6, 5 — bigger or smaller each time?", "9, 8, 7, 6, 5, 4, 3, 2, 1, 0: they count down."],
    reveal: "They count down, one at a time, all the way to zero. That is the <b>Nines Code</b>.",
  }),
];

const roundingItems: Item[] = [
  q({
    id: "l3.1:round-left",
    prompt: "Here is a line down the middle of the Map, between the 4 column and the 5 column. How many numbers are on the left of the line?",
    visual: roundingLine(),
    input: { kind: "number" },
    answer: "5",
    hints: ["Count 0, 1, 2… up to the line.", "0, 1, 2, 3, 4.", "0, 1, 2, 3, 4 is five numbers."],
    reveal: "Five.",
  }),
  q({
    id: "l3.1:round-right",
    prompt: "How many on the right?",
    visual: roundingLine(),
    input: { kind: "number" },
    answer: "5",
    hints: ["Count from the line to the end of the row.", "5, 6, 7, 8, 9.", "5, 6, 7, 8, 9 is five numbers."],
    reveal: "Five on each side — 0, 1, 2, 3, 4 go <b>down</b> and 5, 6, 7, 8, 9 go <b>up</b>. Five go down and five go up. It is perfectly fair, and <b>the line decides</b>.",
  }),
  q({
    id: "l3.1:round-548-side",
    prompt: "Round 846 to the nearest 10. Look at the <b>ones</b> digit: 6. Which side of the line is it on?",
    visual: roundingLine(6),
    input: { kind: "choice", options: ["round down", "round up"] },
    answer: "round up",
    hints: ["Find 6 on the row.", "6 is to the right of the line.", "6 is right of the line: round up."],
    reveal: "Round up.",
  }),
  { ...roundItem(846, 10), id: "l3.1:round-548-10", skill: "map-discovery-q", prompt: "So 846 to the nearest 10 is:", reveal: "<b>850.</b> Nearest 10: look at the ones digit." },
  q({
    id: "l3.1:round-548-100-side",
    prompt: "Now round 846 to the nearest 100. This time look at the <b>tens</b> digit: 4. Which side of the line?",
    visual: roundingLine(4),
    input: { kind: "choice", options: ["round down", "round up"] },
    answer: "round down",
    hints: ["Find 4 on the row.", "4 is the last number left of the line.", "4 is left of the line: round down."],
    reveal: "Round down.",
  }),
  { ...roundItem(846, 100), id: "l3.1:round-548-100", skill: "map-discovery-q", prompt: "So 846 to the nearest 100 is:", reveal: "<b>800.</b> Nearest 100: look at the tens digit." },
];

const add9Items: Item[] = [
  q({
    id: "l3.1:add9-move",
    prompt: "Adding 9 uses the same move as counting by nines. Start at 6. Go back 1. Where are you?",
    visual: mathMap({ rows: 2, mark: [6] }),
    input: { kind: "number" },
    answer: "5",
    diagnose: (r) => (is(r, 7) ? { code: "add9-forward", hint: "Back is to the left." } : null),
    hints: ["Back means to the left.", "One square left of 6.", "Back 1 from 6 is 5."],
    reveal: "5.",
  }),
  q({
    id: "l3.1:add9-down",
    prompt: "Now go down a row from 5. Where are you?",
    visual: mathMap({ rows: 2, mark: [5] }),
    input: { kind: "number" },
    answer: "15",
    hints: ["Same column, one row down.", "Under 5 is the teens row.", "One row down from 5 is 15."],
    reveal: "15. So <b>6 + 9 = 15</b>: back 1, down a row.",
  }),
  { ...add9Item(45), id: "l3.1:add9-27", skill: "map-discovery-q", reveal: "54. Back 1 and down, every time." },
  { ...add9Item(0), id: "l3.1:add9-0", skill: "map-discovery-q", reveal: "9. Zero has no back, so the answer is the end of the same line." },
];

const sub9Items: Item[] = [
  q({ id: "l3.1:sub9-1", prompt: "Subtracting 9 is the adding move in reverse: <b>forward 1, then UP a row.</b> Start at 63. Go forward 1. Where are you?", visual: mathMap({ rows: 7, mark: [63] }), input: { kind: "number" }, answer: "64",
    hints: ["Forward means to the right.", "One square right of 63.", "Forward 1 from 63 is 64."], reveal: "64." }),
  q({ id: "l3.1:sub9-2", prompt: "Now go UP a row from 64. Where are you?", visual: mathMap({ rows: 7, mark: [64] }), input: { kind: "number" }, answer: "54",
    hints: ["Same column, one row up.", "Above 64 is the fifties row.", "Up a row from 64 is 54."], reveal: "54. So <b>63 − 9 = 54</b>: forward 1, up a row." }),
  { ...subMapItem(9, 12), id: "l3.1:sub9-14", skill: "map-discovery-q", visual: mapPuzzleUp(12, 1), reveal: "3. Forward 1, up a row: 12 → 13 → 3." },
];

export const L3_LESSON1: Lesson = {
  id: "L3.1",
  level: "3",
  num: 1,
  title: "The Math Map, Number Bonds & The Nines",
  blurb: "The Map starts at zero · the five bonds · counting by nines · rounding · adding 9",
  steps: [
    { kind: "discover", id: "map", title: "The Math Map", intro: "You will not memorize your way through the times tables. You will count your way there, on the Math Map.", items: mapItems },
    { kind: "discover", id: "bonds", title: "The number bonds", intro: "Your fingers already know this.", items: bondItems },
    { kind: "practice", id: "bond-fill", title: "Practice the bonds", skill: "bond-fill", backup: "bonds" },
    { kind: "practice", id: "pm10", title: "Add or subtract 10 — one row up or down", skill: "plus-minus-10", backup: "map" },
    { kind: "discover", id: "nines", title: "Discover the nines", intro: "We start with the nines because they have the clearest code of any table.", items: ninesItems },
    { kind: "tables", id: "tables9", title: "The Nines on the keypad", intro: "Count forward and back, then practice the nines with the keypad and the three anchors.", units: ["t9", "t1112"] },
    { kind: "discover", id: "rounding", title: "Rounding — the line decides", items: roundingItems },
    { kind: "practice", id: "round", title: "Practice rounding", skill: "round-line", backup: "rounding" },
    { kind: "discover", id: "add9", title: "Adding 9 on the Map", items: add9Items },
    { kind: "practice", id: "add9p", title: "Two-square puzzles: add 9", intro: "First the whole series, 1 + 9 through 9 + 9 and 9 + 0 — then two-digit numbers.", skill: "add-n-map-puzzle", backup: "add9", lead: () => [...Array.from({ length: 9 }, (_, i) => add9Item(i + 1)), add9Item(0)] },
    { kind: "discover", id: "sub9", title: "Subtracting 9 — forward 1, up a row", items: sub9Items },
    { kind: "practice", id: "sub9p", title: "Two-square puzzles: subtract 9", intro: "The whole series first, 10 − 9 through 18 − 9 — then two-digit numbers.", skill: "sub-n-map-puzzle/9", backup: "sub9", lead: seriesSub9 },
    { kind: "practice", id: "rev-add", title: "Review: add", skill: "add-2digit-vertical", mastery: { min: 4, need: 4, window: 5 } },
    { kind: "practice", id: "rev-sub", title: "Review: subtract", skill: "sub-2digit-vertical", mastery: { min: 4, need: 4, window: 5 } },
    { kind: "practice", id: "rev-pv", title: "Review: place value", skill: "place-value-underlined", mastery: { min: 3, need: 3, window: 4 } },
    { kind: "practice", id: "rev-cmp", title: "Review: compare", skill: "compare-whole", mastery: { min: 3, need: 3, window: 4 } },
  ],
};
